import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { TrendingUp, Award, ExternalLink, Loader2, MapPin, Star } from "lucide-react";

const RANGES = [
  { days: 7,  label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const fmtDate = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s + "T00:00:00Z");
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch { return s; }
};

/**
 * Affiliate trends — daily click chart + Top-10 leaderboard. Powered by
 * GET /api/admin/affiliate/trends?days=N. Slots into the Admin page next to
 * the existing earnings card.
 */
export default function AffiliateTrendsCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/affiliate/trends?days=${days}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const peakClicks = useMemo(() => {
    if (!data?.series?.length) return 1;
    return Math.max(1, ...data.series.map((d) => d.clicks));
  }, [data]);

  if (loading && !data) {
    return (
      <div className="border hairline bg-white p-6 mb-8 flex items-center gap-2 text-sm text-[#888]" data-testid="trends-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading affiliate trends…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="border hairline bg-white p-6 mb-8" data-testid="affiliate-trends-card">
      <header className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500]"><TrendingUp className="w-3 h-3 inline mr-1" />Affiliate trends</div>
          <h3 className="font-display text-xl mt-1">Daily Pitchup clicks · last {data.days} days</h3>
          <p className="text-xs text-[#666] mt-1">
            {data.total_clicks.toLocaleString()} clicks · {data.avg_per_day} avg/day · peak {data.peak_day.clicks} on {fmtDate(data.peak_day.date)}
          </p>
        </div>
        <div className="flex items-center gap-1 mono text-[11px]" data-testid="trends-range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`px-3 py-1.5 border hairline uppercase tracking-wider transition-colors ${
                days === r.days ? "bg-black text-white border-black" : "hover:bg-[#fff4ee]"
              }`}
              data-testid={`trends-range-${r.days}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {/* CHART — pure CSS bar chart, no extra deps */}
      <div className="relative" data-testid="trends-chart">
        <div className="flex items-end gap-[2px] h-32 border-b hairline pb-1">
          {data.series.map((d) => {
            const h = (d.clicks / peakClicks) * 100;
            const isPeak = d.date === data.peak_day.date && d.clicks > 0;
            return (
              <div
                key={d.date}
                className="flex-1 flex flex-col justify-end group relative"
                title={`${d.date}: ${d.clicks} click${d.clicks === 1 ? "" : "s"}`}
              >
                <div
                  className={`w-full transition-colors ${
                    isPeak ? "bg-[#ff4500]" : "bg-[#222] group-hover:bg-[#ff4500]"
                  }`}
                  style={{ height: `${Math.max(d.clicks > 0 ? 4 : 0, h)}%` }}
                />
                {/* Hover tooltip */}
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black text-white text-[10px] mono px-1.5 py-0.5 whitespace-nowrap z-10">
                  {fmtDate(d.date)} · {d.clicks}
                </div>
              </div>
            );
          })}
        </div>
        {/* X-axis: first / mid / last labels only — keeps things readable */}
        <div className="flex justify-between text-[10px] mono text-[#888] mt-1.5 px-0.5">
          <span>{fmtDate(data.start_date)}</span>
          <span>{fmtDate(data.series[Math.floor(data.series.length / 2)]?.date)}</span>
          <span>{fmtDate(data.end_date)}</span>
        </div>
      </div>

      {/* LEADERBOARD */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h4 className="eyebrow flex items-center gap-1.5">
            <Award className="w-3 h-3" /> Top performers · last {data.days} days
          </h4>
          <span className="text-[10px] mono text-[#888]">
            {data.leaderboard.length} sites · double-down on similar inventory
          </span>
        </div>
        {data.leaderboard.length === 0 ? (
          <p className="text-sm text-[#666] py-6 px-4 bg-[#fafafa] border hairline">
            No clicks yet in this window. Once users start tapping the "Find on Pitchup" buttons, the highest-converting sites will rank here.
          </p>
        ) : (
          <div className="border hairline" data-testid="trends-leaderboard">
            {data.leaderboard.map((row, i) => (
              <Link
                key={row.campsite_id}
                to={`/campsites/${row.campsite_id}`}
                className="flex items-center gap-3 px-3 py-2.5 border-b hairline last:border-b-0 hover:bg-[#fff4ee] transition-colors group"
                data-testid={`trends-row-${i}`}
              >
                <span className="w-6 text-center mono text-[11px] text-[#888]">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm leading-tight truncate group-hover:text-[#ff4500]">{row.name}</div>
                  <div className="flex items-center gap-2 text-[10px] mono text-[#888] mt-0.5 flex-wrap min-w-0">
                    {row.region && (
                      <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                        <MapPin className="w-2.5 h-2.5 shrink-0" strokeWidth={1.75} />
                        <span className="truncate max-w-[140px]">{row.region}</span>
                      </span>
                    )}
                    {row.rating != null && (
                      <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                        <Star className="w-2.5 h-2.5 fill-[#ff4500] text-[#ff4500] shrink-0" strokeWidth={1.5} />
                        {Number(row.rating).toFixed(1)}
                      </span>
                    )}
                    {row.pitchup_listed && (
                      <span className="text-[#ff4500] uppercase tracking-wider whitespace-nowrap">· Pitchup direct</span>
                    )}
                  </div>
                </div>
                <span className="text-right">
                  <span className="font-display text-base">{row.clicks}</span>
                  <span className="text-[10px] mono text-[#888] ml-1">clicks</span>
                </span>
                <ExternalLink className="w-3.5 h-3.5 text-[#bbb] group-hover:text-[#ff4500]" strokeWidth={1.5} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
